import React, { useEffect, useState } from "react";
import Wrapper from "@/layout/Wrapper";
import {
  //Checkbox,
  Col,
  Form,
  Input,
  Row,
  Upload,
  Select,
  Button,
  Tabs,
} from "antd";
// import TagsInput from "@/components/input/TagsInput";
import ButtonComponent from "@/components/button/Button";
import CardComponent from "@/components/card";
import { toast, ToastContainer } from "react-toastify";
import { PostItem, PutItem, GetList } from "@/services/customHelperRequests";
import MainHeading from "@/components/heading/MainHeading";
import IconUploadDocs from "@/components/icons/IconUploadDocs";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { refreshContent, setFormProps } from "@/reducers/commonSlice";
import BackWithLabel from "@/components/common/BackWithLabel";
import ReactQuill, { Quill } from "react-quill";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import "react-quill/dist/quill.snow.css";
import { countryList } from "@/helpers/countryList";
import ImageResize from "@mgreminger/quill-image-resize-module";
import Toaster from "@/components/toaster/Toaster";

const { TabPane } = Tabs;
const { Option } = Select;

// register image resize
window.Quill = Quill;

Quill.register("modules/imageResize", ImageResize);

const extractFileName = (url) => {
  const urlParts = url.split("/");
  return urlParts[urlParts.length - 1];
};

const getImageContentTypeFromURL = (url) => {
  // get image extension
  const fileName = extractFileName(url);
  const extension = fileName.split(".")[1];

  // get image content type
  return `image/${extension}`;
};

const fetchImageAsFile = async (url) => {
  const response = await fetch(url);
  console.log(response.headers.get("content-type"));
  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer]);
  const fileName = extractFileName(url);
  const fileObject = new File([blob], fileName, {
    type: response.headers.get("content-type") ?? (getImageContentTypeFromURL(url) || "image/png"),
  });

  Object.assign(fileObject, {
    preview: URL.createObjectURL(fileObject),
  });

  return fileObject;
};

const CreateEditBlog = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // redux formProps holds data when editing
  const { data } = useSelector((state) => state.common.formProps) || {};

  // UI state
  const [activeTab, setActiveTab] = useState("blog"); // "blog" or "country"
  const [isFeatured, setIsFeatured] = useState(false);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(""); // for single-section "Blog" tab

  // Quill modules
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image", "video"],
      [{ table: "table" }],
      ["clean"],
    ],
    imageResize: {
      displayStyles: {
        backgroundColor: "black",
        border: "none",
        color: "white",
      },
      modules: ["Resize", "DisplaySize", "Toolbar"],
    },
    clipboard: { matchVisual: false },
  };

  // filter for country select
  const filterOption = (input, option) => option.label.toLowerCase().includes(input.toLowerCase());

  // load categories
  useEffect(() => {
    GetList("/common/blog-category")
      .then((resp) => {
        const cats = Object.values(resp).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setCategories(cats);
      })
      .catch(() => {
        setCategories([{ value: 1, label: "Default" }]);
      });
  }, []);

  // when editing an existing blog, populate form
  useEffect(() => {
    if (data?.id) {
      console.log("Editing blog", data);
      // pick tab by presence of countryCode
      const isCountry = !!data.metadata?.countryCode;
      setActiveTab(isCountry ? "country" : "blog");

      // files
      // form.setFieldValue("files", data.files || []);
      // always title
      form.setFieldValue("title", data.title);
      // category only matters for country
      form.setFieldValue("categoryId", data.categoryId);
      // readingTime & featured & keywords always present
      form.setFieldValue("readingTime", data.readingTime);
      setIsFeatured(data.isFeatured);
      setTags(data.keywords || []);
      // authorName is top-level
      form.setFieldValue("authorName", data.authorName);

      try {
        // files: convert URLs to File objects
        console.log(data.files, data.coverImages);
        const filePromises = (data.files || []).map((_, index) => {
          return Promise.resolve(fetchImageAsFile(data.coverImages[index]));
        });
        Promise.all(filePromises).then((files) => {
          form.setFieldValue("files", files);
        });
      } catch (error) {
        console.error("Error fetching files:", error);
      }

      if (isCountry) {
        // subFields for country
        form.setFieldValue("subTitle", data.subTitle);
        form.setFieldValue("contentTitle", data.contentTitle);
        form.setFieldValue(["metadata", "countryCode"], data.metadata.countryCode);
        form.setFieldValue("contents", data.contents);
      } else {
        // single content
        const first = Array.isArray(data.contents) ? data.contents[0] : null;
        const html = first?.sectionContent ?? "";
        setContent(html);
        form.setFieldValue("content", html);
      }
    } else {
      // reset
      form.resetFields();
      setTags([]);
      setIsFeatured(false);
      setContent("");
      setActiveTab("blog");
    }
  }, [data, form]);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const onFinish = async (values) => {
    setLoading(true);

    // build standard FormData
    const fd = new FormData();
    // always present
    fd.append("title", values.title);
    // fd.append("authorName", values.authorName);
    fd.append("readingTime", values.readingTime);
    fd.append("isFeatured", isFeatured);
    // keywords comma list
    if (tags.length) fd.append("keywords", tags.join(","));

    // categoryId: for blog tab we hardcode a non-country cat
    const sendCategoryId =
      activeTab === "country"
        ? values.categoryId
        : categories.find((c) => c.label === "General")?.value || values.categoryId;
    fd.append("categoryId", sendCategoryId);

    // metadata: always include author separately, countryCode only for country
    const meta = { author: values.authorName };
    if (activeTab === "country" && values.metadata?.countryCode) {
      meta.countryCode = values.metadata.countryCode;
    }
    fd.append("metadata", JSON.stringify(meta));

    // contents: array-of-sections
    let sectionsPayload;
    if (activeTab === "country") {
      sectionsPayload = values.contents;
    } else {
      // blog-tab: single section, use title as sectionTitle
      sectionsPayload = [
        {
          sectionTitle: values.title,
          sectionContent: values.content,
        },
      ];
    }
    fd.append("contents", JSON.stringify(sectionsPayload));

    // subTitle + contentTitle for country, otherwise empty
    fd.append("subTitle", activeTab === "country" ? values.subTitle : "");
    fd.append("contentTitle", activeTab === "country" ? values.contentTitle : "");

    // files
    (values.files || []).forEach((f) => {
      const file = f.originFileObj || f;
      if (file instanceof File) fd.append("files", file);
    });

    try {
      toast.loading("Please wait…");
      const resp = data?.id
        ? await PutItem(`users/blog/${data.id}`, fd)
        : await PostItem("users/blog", fd);
      toast.dismiss();
      toast.success("Your blog has been submitted for review");

      setTimeout(() => {
        // reset + navigate
        setIsFeatured(false);
        setTags([]);
        form.resetFields();
        dispatch(setFormProps({}));
        dispatch(refreshContent());
        navigate("/dashboard/my-blogs", { replace: false });
      }, 3000);
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.message || "Error");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Toaster />
      <div className="container">
        <MainHeading title={data?.id ? `Edit Blog: ${data.title}` : "Create New Blog"} />
        <CardComponent>
          {data?.id && <BackWithLabel path="/dashboard/my-blogs" label="Back" />}

          {/* <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <TabPane tab="Blog" key="blog" />
            <TabPane tab="Country" key="country" />
          </Tabs> */}

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              contents: [{ sectionTitle: "", sectionContent: "" }],
            }}
          >
            {/* ─── File/CoverImage ─── */}
            <Form.Item
              label={activeTab === "country" ? "Images/Files" : "Cover Image"}
              name="files"
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              rules={[
                {
                  required: false,
                  // validator: (_, v) =>
                  //   v && v.length
                  //     ? Promise.resolve()
                  //     : Promise.reject("Please upload a file!"),
                },
              ]}
            >
              <Upload.Dragger
                beforeUpload={() => false}
                multiple={activeTab === "country"}
                listType="picture"
              >
                <p className="ant-upload-drag-icon">
                  <IconUploadDocs />
                </p>
                <p className="ant-upload-text">
                  Click or drag {activeTab === "country" ? "files" : "an image"} to upload
                </p>
              </Upload.Dragger>
            </Form.Item>

            {/* ─── Title ─── */}
            <Form.Item
              label="Blog Title"
              name="title"
              rules={[{ required: true, message: "Please enter title!" }]}
            >
              <Input placeholder="Enter blog title…" />
            </Form.Item>

            {activeTab === "country" ? (
              <>
                {/* ─── Subtitle & ContentTitle ─── */}
                <Row gutter={10}>
                  <Col span={12}>
                    <Form.Item
                      label="Subtitle"
                      name="subTitle"
                      rules={[{ required: true, message: "Enter subtitle!" }]}
                    >
                      <Input placeholder="Enter subtitle…" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Content Title"
                      name="contentTitle"
                      rules={[{ required: true, message: "Enter content title!" }]}
                    >
                      <Input placeholder="Enter content title…" />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ─── Dynamic Sections ─── */}
                <Form.List name="contents">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...rest }) => (
                        <div key={key} style={{ marginBottom: 20 }}>
                          <Form.Item
                            {...rest}
                            label="Section Title"
                            name={[name, "sectionTitle"]}
                            rules={[{ required: true }]}
                          >
                            <Input placeholder="Section title" />
                          </Form.Item>
                          <Form.Item
                            {...rest}
                            label="Section Content"
                            name={[name, "sectionContent"]}
                            rules={[{ required: true }]}
                          >
                            <ReactQuill modules={modules} />
                          </Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => remove(name)}
                            icon={<MinusCircleOutlined />}
                            disabled={fields.length === 1}
                            style={{ marginBottom: 16 }}
                          >
                            Remove section
                          </Button>
                        </div>
                      ))}
                      <Form.Item>
                        <Button type="dashed" block onClick={() => add()} icon={<PlusOutlined />}>
                          Add Section
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>

                {/* ─── Category & CountryCode ─── */}
                <Row gutter={10}>
                  <Col span={12}>
                    <Form.Item label="Category" name="categoryId" rules={[{ required: true }]}>
                      <Select placeholder="Select category">
                        {categories.map((c) => (
                          <Option key={c.value} value={c.value}>
                            {c.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Country"
                      name={["metadata", "countryCode"]}
                      rules={[{ required: true }]}
                    >
                      <Select showSearch placeholder="Select country" filterOption={filterOption}>
                        {countryList.map((c) => (
                          <Option key={c.isoCode} value={c.isoCode} label={c.name}>
                            <span style={{ display: "flex", alignItems: "center" }}>
                              <img
                                src={c.flag}
                                alt={c.name}
                                style={{ width: 24, marginRight: 8 }}
                              />
                              {c.name}
                            </span>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : (
              /* ─── Single Content Editor ─── */
              <Form.Item
                label="Content"
                name="content"
                rules={[{ required: true, message: "Please enter content!" }]}
              >
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={(html) => {
                    setContent(html);
                    form.setFieldValue("content", html);
                  }}
                  modules={modules}
                />
              </Form.Item>
            )}

            {/* ─── Shared Fields (both tabs) ─── */}
            <Row gutter={10}>
              <Col span={12}>
                <Form.Item
                  label="Reading Time (mins)"
                  name="readingTime"
                  rules={[{ required: true, message: "Enter read time!" }]}
                >
                  <Input type="number" placeholder="e.g. 5" />
                </Form.Item>
              </Col>
              {/* HIDDEN */}
              {/* <Col span={12}> 
                <Form.Item
                  label="Author Name"
                  name="authorName"
                  rules={[{ required: false, message: "Enter author name!" }]}
                >
                  <Input placeholder="Author name" />
                </Form.Item>
              </Col> */}
            </Row>

            {/* <Row gutter={10}>
              <Col span={12}>
                <Form.Item label="Featured" name="isFeatured">
                  <Checkbox
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Keywords" name="keywords">
                  <TagsInput tags={tags} setTags={setTags} />
                </Form.Item>
              </Col>
            </Row> */}

            <Row justify="center" style={{ marginTop: 20 }}>
              <ButtonComponent
                htmlType="submit"
                type="primary"
                label={data?.id ? "Update Blog" : "Publish Blog"}
                loading={loading}
                style={{ width: 300 }}
              />
            </Row>

            {!data?.id && (
              <Row justify="center" style={{ marginTop: 12 }}>
                <ButtonComponent
                  onClick={() => navigate("/dashboard/my-blogs")}
                  type="default"
                  label="View my blogs"
                  style={{ width: 300 }}
                />
              </Row>
            )}
          </Form>
        </CardComponent>
      </div>
      <ToastContainer />
    </Wrapper>
  );
};

export default CreateEditBlog;
